import React from 'react';
import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import {css} from 'aphrodite';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/fontawesome-pro-solid';
import Markdown from '../Markdown';
import AutoLoginButton from '../Login/autoLoginButton';
import {renderModalField} from 'libs/components';
import {getResolvedLoginSettings} from 'services/authentication';

export const RedirectModal = (props) => {
  const {
    settings,
    styleSheet,
    handleExit,
    showRedirectModal,
  } = props;

  return (
    <Modal
      data-testid="redirect-modal"
      show={showRedirectModal}
      onHide={handleExit}
      aria-labelledby={'redirect-modal-title'}
      backdrop={'static'}
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={'pal-modal'}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'redirect-modal-title'} className={'redirect-modal-title'}>
          {settings.page.redirectLinkModalTitle ? settings.page.redirectLinkModalTitle : 'This site is for US healthcare providers'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'redirect-modal-body'}>
        {settings.page.redirectLinkText}
      </Modal.Body>
      <Modal.Footer className={'pal-modal-footer'}>
        <Button className={css(styleSheet.buttonPrimary, styleSheet.modalSuccess)} onClick={handleExit}>Okay</Button>
      </Modal.Footer>
    </Modal>
  );
};

RedirectModal.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  handleExit: PropTypes.func.isRequired,
  showRedirectModal: PropTypes.bool,
};

export const LoginModal = (props) => {
  const {
    settings,
    styleSheet,
    handleHide,
    handleRedirect,
    showLoginModal,
  } = props;

  const loginSettings = getResolvedLoginSettings(settings);
  const errorMessages = {
    apiUnavailable: loginSettings.error.apiUnavailable,
    npiIncorrect: loginSettings.error.npiIncorrect
  };

  return (
    <Modal
      data-testid="login-modal"
      show={showLoginModal}
      onHide={handleHide}
      aria-labelledby={'healthcare-modal-title'}
      backdrop={'static'}
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={'pal-modal'}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'healthcare-modal-title'} className={'healthcare-modal-title'}>
          {settings.content.physicianVerificationPopup}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'healthcare-modal-body'}>
        {settings.content.physicianVerificationBanner}
      </Modal.Body>
      <Modal.Footer className={'pal-modal-footer'}>
        <Button className={css(styleSheet.buttonSecondary, styleSheet.modalReject)} onClick={handleRedirect}>{settings.content.physicianVerificationReject}</Button>
        <Button className={css(styleSheet.buttonPrimary, styleSheet.modalSuccess)} onClick={handleHide}>{settings.content.physicianVerificationAccept}</Button>
        { settings.autoLoginButton?.disabled != null && !settings.autoLoginButton.disabled &&
          <AutoLoginButton className={css(styleSheet.buttonPrimary, styleSheet.modalSuccess)} buttonLabel='Other' npiNumber={loginSettings.autoLoginNpi} errorMessages={errorMessages}></AutoLoginButton>
        }
      </Modal.Footer>
    </Modal>
  );
};

LoginModal.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  handleHide: PropTypes.func.isRequired,
  handleRedirect: PropTypes.func.isRequired,
  showLoginModal: PropTypes.bool,
};

export const ExitModal = (props) => {
  const {
    settings,
    styleSheet,
    handleHide,
    handleOpenDocument,
    showExitModal,
    isResolving,
  } = props;

  return (
    <Modal
      data-testid="exit-modal"
      show={showExitModal}
      onHide={handleHide}
      aria-labelledby={'exit-modal-title'}
      backdrop={true}
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={'pal-modal'}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'exit-modal-title'} className={'exit-modal-title'}>
          <Markdown>
            {settings.content.interstitialHeader}
          </Markdown>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'exit-modal-body'}>
        {settings.content.interstitial}
      </Modal.Body>
      <Modal.Footer className={'pal-modal-footer'}>
        <Button className={css(styleSheet.buttonSecondary, styleSheet.interstitialModalReject)} onClick={handleHide}>{settings.content.interstitialReject}</Button>
        <Button className={css(styleSheet.buttonPrimary, styleSheet.interstitialModalSuccess)} onClick={handleOpenDocument} disabled={isResolving}>{isResolving ? <FontAwesomeIcon icon={faSpinner} spin size="lg" /> : settings.content.interstitialAccept}</Button>
      </Modal.Footer>
    </Modal>
  );
};

ExitModal.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  handleHide: PropTypes.func.isRequired,
  handleOpenDocument: PropTypes.func.isRequired,
  showExitModal: PropTypes.bool,
  isResolving: PropTypes.bool,
};

export const FeatureModal = (props) => {
  const {
    settings,
    styleSheet,
    handleHide,
    showFeatureModal,
  } = props;

  return (
    <Modal
      data-testid="feature-modal"
      show={showFeatureModal}
      onHide={handleHide}
      aria-labelledby={'feature-modal-title'}
      backdrop={true}
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={'pal-modal'}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'feature-modal-title'} className={'feature-modal-title'}>
          {settings.content.newFeatureBanner}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'feature-modal-body'}>
        <Markdown>
          {settings.content.newFeaturePopup.markdown}
        </Markdown>
      </Modal.Body>
      <Modal.Footer className={'pal-modal-footer'}>
        <Button className={css(styleSheet.buttonPrimary, styleSheet.modalSuccess)} onClick={handleHide}>Okay</Button>
      </Modal.Footer>
    </Modal>
  );
};

FeatureModal.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  handleHide: PropTypes.func.isRequired,
  showFeatureModal: PropTypes.bool,
};

export const DisclaimerModal = (props) => {
  const {
    settings,
    styleSheet,
    handleHide,
    showDisclaimerModal,
  } = props;

  return (
    <Modal
      data-testid="disclaimer-modal"
      show={showDisclaimerModal}
      onHide={handleHide}
      aria-labelledby={'disclaimer-modal-title'}
      backdrop={true}
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={'pal-modal'}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'disclaimer-modal-title'} className={'disclaimer-modal-title'}>
          {settings.table.disclaimerModal.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'disclaimer-modal-body'}>
        <Markdown>
          {settings.table.disclaimerModal.content.markdown}
        </Markdown>
      </Modal.Body>
      <Modal.Footer className={'pal-modal-footer'}>
        <Button className={css(styleSheet.buttonPrimary, styleSheet.modalSuccess)} onClick={handleHide}>
          {settings.table.disclaimerModal.disclaimerAccept}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DisclaimerModal.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  handleHide: PropTypes.func.isRequired,
  showDisclaimerModal: PropTypes.bool,
};

export const ContactInstructionsModal = (props) => {
  const {
    rowData,
    settings,
    styleSheet,
    handleHide,
    showContactInstructionsModal,
  } = props;

  if (!rowData) {
    return null;
  }

  return (
    <Modal
      show={showContactInstructionsModal}
      onHide={handleHide}
      aria-labelledby={'contact-instructions-modal-title'}
      backdrop={true}
      size="lg"
      backdropClassName={css(styleSheet.modalBackdrop)}
      dialogClassName={`pal-modal ${css(styleSheet.contactInstructionsModal)}`}
      className={css(styleSheet.modal)}
    >
      <Modal.Header closeButton>
        <Modal.Title id={'contact-instructions-modal-title'} className={'contact-instructions-modal-title'}>
          {rowData &&
            settings.table.contactInstructionsModal.title
          }
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={'contact-instructions-modal-body'}>
        {
          <div>
            {rowData.contactinstructions &&
              <label className={css(styleSheet.contactInstructionsModalBodyinstructions)}>
                <span>{rowData.contactinstructions}</span>
              </label>
            }
            {settings.table.contactInstructionsModal.body.map((block) => {
              return renderModalField(
                block,
                rowData,
                styleSheet,
                `contactInstructionsModalBody${block.property}`,
                'contactInstructionsModalLabel'
              );
            })}
          </div>
        }
      </Modal.Body>
    </Modal>
  );
};

ContactInstructionsModal.protoTypes = {
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  showContactInstructionsModal: PropTypes.bool,
};